import React, { FC, useState } from 'react'
import { Field, initialize, InjectedFormProps, reduxForm } from 'redux-form'
import { Form, Collapse, Row, Col, Button } from 'antd'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../reducers'

// components
import SQLEditorModal from '../../modals/SQLEditorModal'

// atoms
import InputField from '../../../atoms/InputField'
import TypeRadioGroupField from '../../../atoms/TypeRadioGroupField'
import SelectField from '../../../atoms/SelectField'
import ColorPickerField from '../../../atoms/ColorPickerField'
import InputNumberField from '../../../atoms/InputNumberField'

// interfaces
import { IFormItemData } from '../../../types/interfaces'

// utils
import { ELEMENT_ATTRIBUTES, ELEMENT_TYPES, FORM, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT, RADIO_BTN_GROUP_FIELD } from '../../../utils/enums'

// assets
import { ReactComponent as ChevronIcon } from '../../../assets/icons/BaseIcons/chevron-down-1-icon.svg'
import { ReactComponent as HeightIcon } from '../../../assets/icons/BaseMiniIcons/height-icon.svg'
import { ReactComponent as WidthIcon } from '../../../assets/icons/BaseMiniIcons/width-icon.svg'
import { ReactComponent as TrashIcon } from '../../../assets/icons/BaseIcons/trash-1-icon.svg'
import ValidationModal from '../../modals/ValidationModal'

const { Panel } = Collapse

export type ComponentProps = {
	onRemoveItem: Function
}
type Props = InjectedFormProps<IFormItemData, ComponentProps> &
	ComponentProps & {
		handleSubmit: Function
	}

const SelectFromTableElementForm: FC<Props> = (props) => {
	const { t } = useTranslation()
	const dispatch: any = useDispatch()
	const { handleSubmit, onRemoveItem } = props
	const [isSQLEditorModalShowed, setSQLEditorModalShowed] = useState(false)
	const [isValidationModalShowed, setValidationModalShowed] = useState(false)
	const disable = useSelector((state: RootState) => state.formTemplate.disable)
	const columns = useSelector((state: RootState) => state.formTemplate.columns)
	const formValues = useSelector((state: RootState) => state.form?.[FORM.SELECT_FROM_TABLE_ELEMENT_FORM]?.values)

	return (
		<>
			<Form layout={'vertical'} onSubmitCapture={handleSubmit}>
				<Row className={'mx-4 mb-4'} justify={'space-between'}>
					<h3>{t('loc:Select from table')}</h3>
					<TrashIcon className={'trashIcon'} onClick={() => onRemoveItem(formValues?.i)} />
				</Row>
				<Row className={'ml-4 mr-4 mb-4'} justify={'center'}>
					<Col span={24}>
						<Field
							component={SelectField}
							label={t('loc:Column in table')}
							getPopupContainer={(triggerNode: any) => triggerNode}
							name={ELEMENT_ATTRIBUTES.TABLE_COLUMN}
							options={columns?.data}
							size={'medium'}
							disabled={disable}
						/>
					</Col>
				</Row>
				<Collapse expandIcon={({ isActive }) => <ChevronIcon rotate={isActive ? 0 : 180} />} expandIconPosition={'end'} ghost>
					<Panel header={t('loc:Size')} key='1'>
						<Row justify={'center'} className={'mt-2 mb-2 w-full'}>
							<Col span={11}>
								<Field
									className={'element-width'}
									component={InputField}
									name={ELEMENT_ATTRIBUTES.COL}
									size={'medium'}
									suffix={<WidthIcon />}
									disabled={disable}
									maxLength={2}
								/>
							</Col>
							<Col span={2} />
							<Col span={11}>
								<Field
									className={'element-height'}
									component={InputField}
									name={ELEMENT_ATTRIBUTES.ROW}
									size={'medium'}
									suffix={<HeightIcon />}
									disabled={true}
									maxLength={3}
								/>
							</Col>
						</Row>
					</Panel>
					<Panel header={t('loc:Label')} key='2'>
						<Field component={InputField} placeholder={t('loc:Enter label')} name={ELEMENT_ATTRIBUTES.LABEL} size={'medium'} disabled={disable} />
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col className={'h-34px'} span={11}>
								<Field component={ColorPickerField} name={ELEMENT_ATTRIBUTES.LABEL_COLOR} size={'medium'} disabled={disable} />
							</Col>
							<Col span={2} />
							<Col span={11}>
								<Field
									component={TypeRadioGroupField}
									optionsType={RADIO_BTN_GROUP_FIELD.TEXT_TYPE}
									name={ELEMENT_ATTRIBUTES.LABEL_TEXT_TYPE}
									disabled={disable}
								/>
							</Col>
						</Row>
					</Panel>
					<Panel header={t('loc:Table content')} key='3'>
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={15} className={'h-34px flex flex-col justify-center'}>
								<p className={'m-0'}>{t('loc:Max selected rows')}</p>
							</Col>
							<Col span={2} />
							<Col span={7} className={'h-34px'}>
								<Field
									className={'w-full'}
									component={InputNumberField}
									name={ELEMENT_ATTRIBUTES.MAX_SELECTED_ROWS}
									size={'medium'}
									disabled={disable}
								/>
							</Col>
						</Row>
						<Button
							className={'unfilled-btn w-full h-25px mb-2 mt-2'}
							disabled={disable}
							onClick={() => {
								setSQLEditorModalShowed(true)
								dispatch(
									initialize(FORM.SQL_EDITOR_FORM, {
										[ELEMENT_ATTRIBUTES.SQL]: formValues?.[ELEMENT_ATTRIBUTES.SQL] || '',
										[ELEMENT_ATTRIBUTES.LINKED_FIELDS]: formValues?.[ELEMENT_ATTRIBUTES.LINKED_FIELDS]
									})
								)
							}}
						>
							{t('loc:SQL editor')}
						</Button>
					</Panel>
					<Panel header={t('loc:Validations')} key='4'>
						<Button
							className={'unfilled-btn w-full h-25px mb-2'}
							disabled={disable}
							onClick={() => {
								setValidationModalShowed(true)
							}}
						>
							{t('loc:Validation options')}
						</Button>
					</Panel>
				</Collapse>
			</Form>
			<ValidationModal
				width={460}
				typeOfElementForm={FORM.SELECT_FROM_TABLE_ELEMENT_FORM}
				isVisible={isValidationModalShowed}
				onCancel={() => {
					setValidationModalShowed(false)
				}}
				elementLabel={formValues?.label}
				elementType={ELEMENT_TYPES.SELECT_FROM_TABLE}
			/>
			<SQLEditorModal
				width={460}
				typeOfElementForm={FORM.SELECT_FROM_TABLE_ELEMENT_FORM}
				isVisible={isSQLEditorModalShowed}
				onCancel={() => {
					setSQLEditorModalShowed(false)
				}}
			/>
		</>
	)
}

const form = reduxForm<IFormItemData, ComponentProps>({
	form: FORM.SELECT_FROM_TABLE_ELEMENT_FORM,
	forceUnregisterOnUnmount: true,
	touchOnChange: true,
	destroyOnUnmount: true,
	onChange: debounce((values, dispatch, props) => {
		if (props.anyTouched) {
			props.submit()
		}
	}, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT)
})(SelectFromTableElementForm)

export default form
